/**
 * PDF Preview Component
 *
 * Displays PDF file previews with thumbnails, upload progress, and file info.
 *
 * @module features/chat/components/preview/PdfPreview
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { File, LoaderCircle, X as XIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { isStoragePdfBlock } from "@/lib/storage";
import type {
  Base64ContentBlock,
  StoragePdfBlock,
} from "../../types/messages";

// =============================================================================
// TYPES
// =============================================================================

export interface PdfPreviewProps {
  block: StoragePdfBlock | Base64ContentBlock;
  removable?: boolean;
  onRemove?: () => void;
  className?: string;
  size?: "sm" | "md" | "lg";
}

// =============================================================================
// COMPONENT
// =============================================================================

export const PdfPreview: React.FC<PdfPreviewProps> = ({
  block,
  removable = false,
  onRemove,
  className,
  size = "md",
}) => {
  const { t } = useTranslation("chat");

  // Storage PDF block
  if (isStoragePdfBlock(block)) {
    const isUploading = block.uploading === true;
    const uploadProgress = block.uploadProgress;
    const filename =
      block.metadata?.filename || block.metadata?.name || t("multimodal.pdfFile");

    return (
      <div
        className={cn(
          "bg-muted relative flex items-start gap-2 border px-3 py-2",
          className
        )}
      >
        {block.thumbnail ? (
          <img
            src={`data:image/jpeg;base64,${block.thumbnail}`}
            alt={t("multimodal.pdfPreview")}
            className={cn(
              "rounded object-cover flex-shrink-0",
              size === "sm" ? "h-12 w-12" : "h-16 w-16",
              isUploading && "opacity-60"
            )}
            title={t("multimodal.pdfThumbnailTitle")}
          />
        ) : (
          <div
            className={cn(
              "flex flex-shrink-0 flex-col items-start justify-start",
              isUploading && "opacity-60"
            )}
          >
            <File
              className={cn(
                "text-teal-700",
                size === "sm" ? "h-5 w-5" : "h-7 w-7"
              )}
            />
          </div>
        )}
        <span
          className={cn(
            "text-foreground min-w-0 flex-1 text-base break-all",
            isUploading && "opacity-60"
          )}
          style={{ wordBreak: "break-all", whiteSpace: "pre-wrap" }}
        >
          {String(filename)}
        </span>
        {isUploading && (
          <div className="flex items-center gap-2">
            <LoaderCircle className="h-4 w-4 animate-spin text-utrecht-gold" />
            <span className="text-xs text-muted-foreground">
              {uploadProgress ?? 0}%
            </span>
          </div>
        )}
        {removable && (
          <button
            type="button"
            className="bg-background hover:bg-muted ml-2 self-start rounded-full p-1 text-teal-700 cursor-pointer"
            onClick={onRemove}
            aria-label={t("multimodal.removePdf")}
          >
            <XIcon className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }

  // Base64 PDF block (during upload transition)
  const base64Block = block as Base64ContentBlock;
  if (
    base64Block.type === "file" &&
    base64Block.source_type === "base64" &&
    base64Block.mime_type === "application/pdf"
  ) {
    const isUploading = (base64Block as Base64ContentBlock & { uploading?: boolean }).uploading === true;
    const uploadProgress = (base64Block as Base64ContentBlock & { uploadProgress?: number }).uploadProgress;
    const filename =
      base64Block.metadata?.filename ||
      base64Block.metadata?.name ||
      t("multimodal.pdfFile");

    return (
      <div
        className={cn(
          "bg-muted relative flex items-start gap-2 border px-3 py-2",
          className
        )}
      >
        {base64Block.thumbnail ? (
          <img
            src={`data:image/jpeg;base64,${base64Block.thumbnail}`}
            alt={t("multimodal.pdfPreview")}
            className={cn(
              "rounded object-cover flex-shrink-0",
              size === "sm" ? "h-12 w-12" : "h-16 w-16",
              isUploading && "opacity-60"
            )}
            title={t("multimodal.pdfThumbnailTitle")}
          />
        ) : (
          <div
            className={cn(
              "flex flex-shrink-0 flex-col items-start justify-start",
              isUploading && "opacity-60"
            )}
          >
            <File
              className={cn(
                "text-teal-700",
                size === "sm" ? "h-5 w-5" : "h-7 w-7"
              )}
            />
          </div>
        )}
        <span
          className={cn(
            "text-foreground min-w-0 flex-1 text-base break-all",
            isUploading && "opacity-60"
          )}
          style={{ wordBreak: "break-all", whiteSpace: "pre-wrap" }}
        >
          {String(filename)}
        </span>
        {isUploading && (
          <div className="flex items-center gap-2">
            <LoaderCircle className="h-4 w-4 animate-spin text-utrecht-gold" />
            <span className="text-xs text-muted-foreground">
              {uploadProgress ?? 0}%
            </span>
          </div>
        )}
        {removable && (
          <button
            type="button"
            className="bg-background hover:bg-muted ml-2 self-start rounded-full p-1 text-teal-700 cursor-pointer"
            onClick={onRemove}
            aria-label={t("multimodal.removePdf")}
          >
            <XIcon className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }

  return null;
};
